import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { InitialStartupService } from '../../auth/initial-startup.service';
import { selectAuthenticatedUser } from '../../store/user/user.selectors';
import { ModalConfiguration } from '../dynamic/models/modal.model';
import { DynamicLoaderService } from '../dynamic/services/dynamic-loader.service';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { LoaderService } from '../layout/services/loader.service';
import { TokenDto } from '../models/franchise/franchise.model';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  /**
   * True while a connection-class dialog (offline / server unreachable) is on screen.
   *
   * Losing the network fails every in-flight request at once, and
   * `DynamicLoaderService` stacks one modal per call, so without this one offline
   * page load would bury the user under a pile of identical dialogs. The flag is
   * cleared when the dialog is closed, and again on the first successful response —
   * the latter is the safety net for a dialog destroyed some other way
   * (`removeAllDynamicComponents()` on a back-navigation, for instance), so a lost
   * close event cannot silence connection errors for the rest of the session.
   */
  private connectionDialogOpen = false;

  constructor(
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private authService: InitialStartupService,
    private dynamicLoaderService: DynamicLoaderService,
    private store: Store<{ user: TokenDto }>
  ) {}

  // Mirrors the check AuthInterceptorService uses to decide whether to attach a
  // Bearer token. If it sent no token, a 401 is expected rather than a session
  // expiry, and must not end a session that never started.
  private hasActiveSession(): boolean {
    let authenticated = false;
    this.store
      .select(selectAuthenticatedUser)
      .pipe(take(1))
      .subscribe((isAuthenticated) => (authenticated = isAuthenticated));
    return authenticated;
  }

  /**
   * Offline and "the server never answered" are the same HTTP status here, so the
   * browser's own connectivity flag is what separates the two messages.
   */
  private connectionMessage(): string {
    return this.translateService.instant(navigator.onLine ? 'errors.server_unreachable' : 'errors.no_connection');
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.loaderService.show();
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.connectionDialogOpen = false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // 'Skip-Error' and 'Suppress-Error-Dialog' both mean "no popup, the caller
        // handles this one". Re-throw the original HttpErrorResponse so the caller
        // can read the status and the body.
        if (req.headers.has('Skip-Error') || req.headers.has('Suppress-Error-Dialog')) {
          return throwError(() => error);
        }

        let errorMessage = '';
        let showCopy = false;
        let errorCode = undefined;
        let isWarning = false;
        let isPopup = false;
        let isConnectionError = false;
        const applicationError = error.headers.get('Application-Error');

        if (applicationError) {
          errorMessage = applicationError;
        } else {
          switch (error.status) {
            case 400:
            case 500:
              errorCode = error.status;
              errorMessage = error.error;
              showCopy = true;
              isPopup = true;
              break;
            case 401:
              errorCode = error.status;
              // Not a credentials failure. Both 401 and 403 used to render
              // `login.unauthorized` — "the username or password is incorrect" — which is
              // nonsense on an SSO portal that has no password field, and misleading anywhere
              // else: by the time a request 401s the user has already signed in successfully.
              errorMessage = this.translateService.instant('errors.session_expired');
              isWarning = true;
              isPopup = true;
              // logout() does window.location.replace('/auth'), a full document reload.
              // Fired from /auth for a request that was never authenticated, that reload
              // re-bootstraps the app, re-issues the same request and 401s again — an
              // infinite redirect loop. Only end a session that actually exists.
              if (this.hasActiveSession()) {
                this.authService.logout();
              }
              break;
            case 403:
              errorCode = error.status;
              // 403 is authorization, not authentication: the session is fine, the user simply
              // may not do this. Telling them to sign in again sends them down a dead end.
              errorMessage = this.translateService.instant('errors.forbidden');
              isWarning = true;
              isPopup = true;
              break;
            case 404:
              errorCode = error.status;
              errorMessage = error.error;
              isWarning = true;
              isPopup = true;
              break;
            case 408:
              errorCode = error.status;
              errorMessage = this.translateService.instant('errors.request_timeout');
              isWarning = true;
              isPopup = true;
              break;
            case 422:
              errorCode = error.status;
              if (typeof error.error === 'object') {
                Object.keys(error.error).forEach((key, id, array) => {
                  if (id === array.length - 1) {
                    errorMessage += `${error.error[key].toString()}`;
                  } else {
                    errorMessage += `${error.error[key].toString()}<br><br>`;
                  }
                });
              } else {
                errorMessage = error.error;
              }
              isWarning = false;
              isPopup = true;
              break;
            case 502:
            case 503:
              errorCode = error.status;
              errorMessage = this.translateService.instant('errors.server_unavailable');
              isWarning = true;
              isPopup = true;
              isConnectionError = true;
              break;
            // Status 0 is a raw network failure (no service worker controlling the page).
            // With the service worker active that same failure arrives as a synthetic 504:
            // ngsw-worker.js `Driver.safeFetch` turns every rejected fetch into
            // `504 Gateway Timeout`, which is why going offline used to fail silently on
            // QA/Staging/Prod but not in local dev — AB#96121.
            case 0:
            case 504:
              errorCode = error.status;
              if (error.status === 504 && req.headers.has('Timout-Error')) {
                // Opt-in marker from long-running batch endpoints: the work keeps going
                // server-side, so this is "come back later", not a connection failure.
                errorMessage = this.translateService.instant('errors.time_out');
              } else {
                errorMessage = this.connectionMessage();
                isConnectionError = true;
              }
              isWarning = true;
              isPopup = true;
              break;
            default:
              // Anything not handled above still has to say something — never fail silently.
              errorCode = error.status;
              errorMessage = error.message || error.error;
              isWarning = true;
              isPopup = true;
              break;
          }
        }

        if (isPopup && isConnectionError) {
          if (this.connectionDialogOpen) {
            isPopup = false;
          } else {
            this.connectionDialogOpen = true;
          }
        }

        if (isPopup) {
          const configuration: ModalConfiguration = {
            panelClass: 'popup small',
            animation: 'scale',
            outsideClick: false,
          };
          const data = {
            code: errorCode,
            message: errorMessage || this.translateService.instant('errors.not_found'),
            showCopy: showCopy,
            isWarning: isWarning,
          };
          this.dynamicLoaderService
            .open<ErrorDialogComponent>(ErrorDialogComponent, configuration, data)
            .then((container) => {
              return container.componentRef?.instance.closeClick.subscribe(() => {
                isPopup = false;
                if (isConnectionError) {
                  this.connectionDialogOpen = false;
                }
                container.close();
              });
            })
            .catch(() => {
              if (isConnectionError) {
                this.connectionDialogOpen = false;
              }
            });
        }
        return throwError(() => errorMessage);
      }),
      finalize(() => {
        this.loaderService.hide();
      })
    );
  }
}

export const ErrorInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ErrorInterceptor,
  multi: true,
};
